import { StepResult } from "./step-result";
import { OverviewTextSet } from "../services/risk-assessment.service";
import { DecodedQRCode } from "./qr-code.model";
import { SettingDTO } from "./settings.model";

export enum HttpErrorsEnum {
  GENERAL_ERROR = "General error",
  UNKNOWN_CUSTOMER = "unknown customer",
  AUTHENTICATION_REQUIRED = "authentication required",
  CANT_LOGIN = "cant login",
  POPUP_DISABLED = "popup disabled",
  FAILED_TO_FETCH_DATA = "failed to fetch data",
  TOKEN_EXPIRED = "token expired",
  INVALID_TOKEN = "invalid token",
  TRUSTED_LIMIT = "You've reached the limit of trusted domains/email addresses",
}

export enum CacheNamesEnum {
  ITEM_CHANGED = "ItemChanged",
  TOKEN_TYPE = "TokenType",
  LAST_UPDATE_DT = "LastUpdateDT",
  USER = "User",
  SETTINGS = "Settings",
  POINTS = "FlowPoints",
  OVERVIEW_SECTIONS = "OverviewSections",
  FREE_EMAIL_SERVICE = "FreeEmailService",
  TRUSTED_DOMAINS = "TrustedDomains",
  TRUSTED_TOP_LEVEL_DOMAINS = "TrustedTopLevelDomains",
  DANGEROUS_TOP_LEVEL_DOMAINS = "DangerousTopLevelDomains",
  BLACKLIST_DOMAINS = "BlackListDomains",
  DANGEROUS_FILE_EXTENSIONS = "DangerousFileExtensions",
  PERSONAL_TRUSTED_DOMAINS = "PersonalTrustedDomains",
  LANGUAGE = "language",
  TERMS_AND_CONDITION = "TermsAndConditions",
  ETAG = "Etag"
}

export enum EtagEntitiesEnum {
  BATCH_ALL = "BATCH_ALL",
  SETTINGS = "SETTINGS",
  PROFILE = "PROFILE",
  TERMS_AND_CONDITION = "TERMS_AND_CONDITION",
}

export interface EtagCacheModel {
  etagValues: Record<EtagEntitiesEnum, string>;
}

export enum Constants {
  FAILED_DOMAIN_AGE = "Failed",
  NOT_AVAILABLE_DOMAIN_AGE = "Info Not Available",
  NOT_PROVIDED = "Not Provided",
  FREE_SERVICE = "Free Service",
  TRUSTED_DOMAIN = "Trusted",
  DANGEROUS_DOMAIN = "Dangerous",
  STANDARD_DOMAIN = "Standard",
  CONDITIONS_GROUP = "Conditions",
  DOMAIN_CONFIG_CONDITION = "Domain Config",
  DOMAIN_ORIGIN_CONDITION = "Domain Origin",
  DEFAULT_API_CALL_TIMEOUT = 10000,
  PROFILE_API_CALL_TIMEOUT = 20000,
  DEFAULT_CHECK_TIMEOUT_BEFORE_PENDING = 5000,
  MAIL_CHANGE_DEBOUNCE = 500,
}

export enum TokenTypesEnum {
  OFFICE_GET_CALLBACK_TOKEN,
  NAA_TOKEN
}

export enum EvaluationFlowsEnum {
  SENDER = "Sender",
  IMPERSONATION_RISK = "ImpersonationRisk",
  LINKS = "Links",
  ATTACHMENTS = "Attachments",
  QRCODE = "QRCodes",
}

export const CUSTOM_SORT = [EvaluationFlowsEnum.SENDER, EvaluationFlowsEnum.IMPERSONATION_RISK,
  EvaluationFlowsEnum.LINKS, EvaluationFlowsEnum.QRCODE, EvaluationFlowsEnum.ATTACHMENTS, Constants.CONDITIONS_GROUP];

export enum ReplyToTypes {
  DIFFERENT = "differentDomain",
  DIFFERENT_BUT_SAME = "differentButSameDomain",
  NONE_OR_SAME = "noneOrSame"
}

export enum NoReplyTypes {
  MATCH = "match",
  NO_MATCH_BUT_TRUSTED = "noMatchDomainTrusted",
  NO_MATCH_AND_UNTRUSTED = "noMatchDomainNotTrusted"
}

export interface IStepConfig {
  id: string;
  process: string;
  description: string;
  nodePaths: {
    [key: string]: NodePath;
  };
}

export interface EvFlow {
  EvaluationFlows: IEvaluationStepsConfig[];
  multipleConditions?: IMultipleConditions[];
}

export interface IEvaluationStepsConfig {
  name: string,
  startId: string,
  steps: IStepConfig[];
}

export interface IMultipleConditions {
  title: string;
  points: number;
  expression?: string;
  conditions: ICondition[];
  overviewText?: OverviewInfo;
  result?: NodeResult;
}

export interface ICondition {
  key?: string;
  flowName: string
  stepId: string
  value: string
}

export interface Node {
  id: string;
  logId: number;
  process: (data?: NodeData, info?: ResultInfo) => Promise<StepResult>;
  description: string;
  nodePaths: { [key: string]: NodePath };
  result?: NodeResult;
}

export interface OverviewMapValue {
  displayOrder: number;
  overviewTextSet: OverviewTextSet;
}

export interface NodeResult {
  points: number,
  decision: string,
  overviewText?: OverviewInfo,
  info?: ResultInfo,
}

export enum AddinTabs{
  OVERVIEW,
  SENDER,
  LINKS,
  ATTACHMENTS,
  CONTENT,
  LOGS
}

export interface OverviewInfo {
  flow?: string,
  section: string;
  value: string;
  htmlComponent: string;
  isMultipleCondition?: boolean;
}

export interface OverviewSectionLink {
  id: string;
  isSectionReasonForConcern: boolean;
  tab: AddinTabs,
  decision?: string,
  flowName?: string,
}

export interface NodePath {
  nextNode: string;
  points: PointResult;
  overviewText?: OverviewInfo;
}

export interface NodeData {
  senderEmail: string;
  domain: string;
  topLevelDomain: string;
  score: number;
  attachment?: Office.AttachmentDetails;
  link?: HTMLAnchorElement;
  qrCode?: DecodedQRCode
}

export interface PointResult {
  value: number;
  name: string;
}

export interface IAttachmentIndex {
  type: string,
  concerns: string[],
  bestPractice: string[]
}

export interface OverviewSectionsDTO{
  id: number;
  name: string;
  display_order: number;
  item_limit: number;
  sorting_criteria: string;
  icon_color: string;
}

export enum OverviewSectionsEnum {
  REASONS_CONC = "Reasons For Concern",
  LESS_SUS = "Less Suspicious",
  GOOD_SIGNS = "Good Signs"
}

export interface OverviewTextReplacements {
  tld: string;
}

export interface FreeServiceResult {
  domain: string;
  service_type: String
  domain_register_dt?: Date;
  created_at?: Date;
}

export class LogLine {
  id: number;
  flow: string;
  check: string;
  result: string;
  reason: string;
  points: number;
  link?: HTMLAnchorElement;
  linkDisplayedAs?: string;
  attachment?: Office.AttachmentDetails;
  qrCode?: DecodedQRCode;

  constructor(id: number, flow: string, check: string, result: string, reason: string, points: number,
              link?: HTMLAnchorElement, linkDisplayedAs?: string, attachment?: Office.AttachmentDetails, qrCode?: DecodedQRCode) {
    this.id = id;
    this.flow = flow;
    this.check = check;
    this.result = result;
    this.reason = reason;
    this.points = points;
    this.link = link;
    this.linkDisplayedAs = linkDisplayedAs;
    this.attachment = attachment;
    this.qrCode = qrCode;
  }
}

export interface ResultInfo {
  error?: string[];
  //Sender-Flow
  sender: string;
  domain: string;
  topLevelDomain: string;
  topLevelDomainCategory?: string;
  isDomainTrusted?: boolean;
  isDomainPersonalTrusted?: boolean;
  isSenderEmailInPersonalTrusted?: boolean;
  smtpOrigin?: string;
  smtpOriginMatchSender?: boolean;
  domainAge?: string;
  domainCreatedAt?: Date;
  isDomainNew?: boolean;
  fromCount?: any;
  toCount?: any;
  replyTo?: string;
  replyToMatchSender?: boolean;

  //Risk-Flow
  isInternal?: boolean;
  dkim?: string;
  spf?: string;
  dmarc?: string;
  dmarcPolicy?: string;
  domainSpfSetup?: boolean;
  domainDmarcSetup?: boolean;
  domainDkimSetup?: boolean;
  compAuth?: string;

  //Links-Flow
  link?: string;
  deceptiveLink?: string;
  displayedAs?: string;
  isConcerning?: boolean;
  linkClearAndDirect?: boolean;
  linkDomainTrusted?: boolean;
  linkSecure?: boolean;
  linkIsConditional?: boolean;
  linkImageUrl?: string;
  linkStyle?: CSSStyleDeclaration;

  //Attachments-Flow
  attachment?: Office.AttachmentDetails;
  attachmentLevel?: number;

  qrCode?: DecodedQRCode;
  qrCodeLinkDomainTrusted?: boolean;
  qrCodeLinkSecure?: boolean;
  qrCodeDeceptiveLink?: string;
}

export enum LinkDisplayedAsTypes {
  FULL_URL = "Full URL",
  TEXT = "Text",
  IMAGE = "Image" ,
  BUTTON = "Button",
  OTHERS = "Others",
}

export interface LinksWithStyles {
  link:   HTMLAnchorElement,
  style: CSSStyleDeclaration,
}


export interface DomainDTO {
  domain: string
  top_level?: boolean
  safe?: boolean
  email?: boolean
  dmarc?: boolean
  spf?: boolean
  dkim?: boolean
  register_dt?: Date
}
export interface DomainRegisterDateDTO {
  created_at?: Date;
  updated_at?: Date;
  domain_name: string;
  company_name?: string;
  spf?: string;
  dmarc?: string;
  dkim?: string;
}

export interface DataDTO {
  points: PointsDTO;
  domains: DomainDTO[];
  free_services: FreeServiceResult[];
  overviews: OverviewSectionsDTO[];
  attachments: DangerousExtensionDTO[];
  settings: SettingDTO[];
  trusted_list: PersonalTrustedDomains
}

export interface PersonalTrustedDomains {
  domains: Map<string, DomainRegisterDateDTO>;
  emails: Map<string, DomainRegisterDateDTO>;
  limits: {
    customer: number,
    user: number,
  }
}

export interface PointsDTO {
  version: string,
  points: EvFlow
}
export interface DangerousExtensionDTO {
  name: string,
  level: number
}
export enum DangerousExtensionLevelEnum {
  low,
  moderate,
  high
}
export interface UserDTO {
  first_name: string;
  last_name: string;
  email: string;
  last_accessed: string;
  licensed: boolean;
  active: boolean;
  upload_expiration_date: Date;
  trusted_domains?: DomainDTO[];
  language: string;
  terms_acceptance_required?: boolean;
}

export interface APIResponse<T> {
  data: T;
  error?: any;
  ok: boolean
}

export enum TabIndexEnum {
  OVERVIEW = 0,
  SENDER = 1,
  LINKS = 2,
  ATTACHMENTS = 3,
  TRUSTED_DOMAINS = 4,
  CONTENT = 5,
  LOGS = 6,
  TERMS_AND_CONDITIONS = 7
}

export const LANGUAGES = new Map<string, { label: string; flagUrl: string }>([
  ['en', { label: 'English', flagUrl: 'https://flagcdn.com/w20/us.png' }],
  ['fr', { label: 'Français', flagUrl: 'https://flagcdn.com/w20/fr.png' }],
]);
